import { FC, useState } from 'react';

import { Box, TextField, Button, Typography, CircularProgress, Chip } from '@mui/material';

import { SelectedUserStatusesEnum } from '../constants';
import { SelectedUser } from '../types';
import { formatUnknownContact, getUserContact, isEmailLike, normalizeContact } from '../utils';

import { UsernameInputProps } from './types';
import { parseUsernames } from './utils';

import { UserModel } from 'v2/types/UserModel';

export const UsernameInput: FC<UsernameInputProps> = ({ selectedUsers, onAddUsers, onValidateUsers, isValidating }) => {
  const [inputValue, setInputValue] = useState('');
  const [validationResults, setValidationResults] = useState<{
    found: UserModel[];
    notFound: string[];
  }>({ found: [], notFound: [] });

  const handleValidate = async () => {
    const usernames = parseUsernames(inputValue);

    if (usernames.length === 0) {
      return;
    }

    const foundUsers = await onValidateUsers(usernames);

    const foundContacts = new Set(
      foundUsers.flatMap((user) =>
        [user.telegramUserName, user.userNickname, user.email]
          .filter((contact): contact is string => !!contact)
          .map(normalizeContact)
      )
    );

    const notFoundUsernames = usernames.filter((username) => !foundContacts.has(normalizeContact(username)));

    setValidationResults({
      found: foundUsers,
      notFound: notFoundUsernames,
    });
  };

  const handleAddValidatedUsers = () => {
    const usersToAdd: SelectedUser[] = [
      ...validationResults.found
        .filter((user) => !selectedUsers.some((selected) => selected.id === user.id))
        .map((user) => ({
          id: user.id,
          username: user.userNickname,
          email: user.email,
          telegramUserName: user.telegramUserName,
          status: SelectedUserStatusesEnum.PENDING,
        })),

      ...validationResults.notFound.map((username) => ({
        id: `not-found-${username}`,
        username,
        ...(isEmailLike(username) ? { email: username } : { telegramUserName: username }),
        status: SelectedUserStatusesEnum.NOT_FOUND,
      })),
    ];

    if (usersToAdd.length > 0) {
      onAddUsers(usersToAdd);
      setInputValue('');
      setValidationResults({ found: [], notFound: [] });
    }
  };

  const hasResults = validationResults.found.length > 0 || validationResults.notFound.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Add users by user nickname, tg username or email
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Enter usernames (one per line or comma separated)&#10;Example:&#10;username1&#10;username2, username3"
        sx={{ mb: 2 }}
        maxRows={100}
      />

      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleValidate}
          disabled={!inputValue.trim() || isValidating}
          startIcon={isValidating && <CircularProgress size={16} />}
        >
          {isValidating ? 'Validating...' : 'Validate users'}
        </Button>

        {hasResults && (
          <Button variant="contained" onClick={handleAddValidatedUsers} disabled={isValidating}>
            Add validated users
          </Button>
        )}
      </Box>

      {hasResults && (
        <Box>
          {validationResults.found.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Found ({validationResults.found.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {validationResults.found.map((user) => (
                  <Chip
                    key={user.id}
                    label={`${getUserContact(user)} (${user.userNickname})`}
                    color="success"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {validationResults.notFound.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Not found ({validationResults.notFound.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {validationResults.notFound.map((username) => (
                  <Chip key={username} label={formatUnknownContact(username)} color="error" size="small" />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                These users will be added as "not found" and skipped during distribution
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
